import { watch, computed, ref, toValue, type CSSProperties, type MaybeRefOrGetter } from 'vue'

export interface Carousel3DOptions {
  itemsCount: MaybeRefOrGetter<number>
  spacing?: number // 项目之间的水平间距
  scaleRatio?: number // 远离中心时每一步的缩放减少比例
  visibleCount?: number // 每侧可见的项目数量
  initialIndex?: number // 初始索引
}

export function useCarousel3D(options: Carousel3DOptions) {
  const currentIndex = ref(options.initialIndex ?? 0)
  const dragOffset = ref(0)

  watch(
    () => toValue(options.itemsCount),
    (len) => {
      if (len === 0) {
        currentIndex.value = 0
        return
      }

      currentIndex.value = ((currentIndex.value % len) + len) % len
    },
    { immediate: true },
  )

  // 供外部使用的归一化索引（0 到 N-1）
  const activeIndex = computed(() => {
    const len = toValue(options.itemsCount)
    if (len === 0) return 0
    return ((currentIndex.value % len) + len) % len
  })

  function next() {
    currentIndex.value++
  }

  function prev() {
    currentIndex.value--
  }

  function goTo(index: number) {
    const len = toValue(options.itemsCount)
    if (len === 0) return
    const current = currentIndex.value

    // 计算 0-N 空间中的原始差异
    const currentMod = ((current % len) + len) % len
    let diff = index - currentMod

    // 调整差异以获取最短路径
    if (diff > len / 2) diff -= len
    else if (diff < -len / 2) diff += len

    currentIndex.value = current + diff
  }

  function setDragOffset(offset: number) {
    dragOffset.value = offset
  }

  function endDrag(threshold = 100) {
    if (dragOffset.value > threshold) {
      prev()
    } else if (dragOffset.value < -threshold) {
      next()
    }
    dragOffset.value = 0
  }

  /**
   * 计算位于 'index' 的项目的样式
   * @param index 项目的原始索引（0 到 N-1）
   * @param spacingOverride 可选的间距覆盖（用于图标 vs 卡片）
   * @param scaleRatioOverride 可选的缩放比例覆盖
   * @param visibleCountOverride 可选的可见数量覆盖
   */
  function getItemStyle(
    index: number,
    spacingOverride?: number,
    scaleRatioOverride?: number,
    visibleCountOverride?: number,
    ignoreDrag: boolean = false,
  ): CSSProperties {
    const len = toValue(options.itemsCount)
    if (len === 0) return {}

    const spacing = spacingOverride ?? options.spacing ?? 300
    const scaleRatio = scaleRatioOverride ?? options.scaleRatio ?? 0.2
    const visibleCount = visibleCountOverride ?? options.visibleCount ?? 2

    const current = currentIndex.value

    // 找到最近的虚拟位置
    const rawDiff = index - current
    const k = -Math.round(rawDiff / len)
    const offset = rawDiff + k * len

    const absOffset = Math.abs(offset)

    if (absOffset > visibleCount) {
      return {
        opacity: 0,
        pointerEvents: 'none',
        visibility: 'hidden',
        position: 'absolute',
        zIndex: -1, // 对应 z-index-hide
      }
    }

    // 100 对应 z-index-carousel，确保内容层级正确
    const zIndex = 100 - absOffset
    const scale = 1 - absOffset * scaleRatio

    // 我们将拖动偏移应用于所有元素。
    // 如果组件希望某些元素（如图标）不跟随拖动，可以通过不改变其 transform 来实现，
    // 但通常图标也应该跟随移动以保持同步。
    // 这里的逻辑假设所有调用此样式的元素都应该响应拖动。

    const translateX = offset * spacing

    const finalTranslateX = translateX + (ignoreDrag ? 0 : dragOffset.value)

    // 透明度逻辑：中心为 1，其他淡出
    const opacity = offset === 0 ? 1 : 1

    return {
      transform: `translateX(${finalTranslateX}px) scale(${scale}) translateZ(0)`,
      zIndex,
      opacity,
      position: 'absolute',
      transition:
        dragOffset.value !== 0 && !ignoreDrag
          ? 'none'
          : 'all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)',
    }
  }

  /**
   * 获取追加图标的样式
   * 始终位于所有项目的最右侧，不随轮播旋转
   */
  function getAppendIconStyle(itemsCount: number, spacingOverride?: number): CSSProperties {
    const spacing = spacingOverride ?? options.spacing ?? 300
    // 计算基于 itemsCount 的最大正向偏移量 (floor(len/2))
    // 假设 items 居中排列，最右侧 item 的偏移量大约是 len/2
    // 例如 len=3 -> maxOffset=1 (spacing)
    // len=4 -> maxOffset=2 (2*spacing)
    const maxOffset = Math.ceil(itemsCount / 2)

    return {
      transform: `translateX(${maxOffset * spacing}px)`,
      zIndex: 90,
      position: 'absolute',
      // 容器高度计算包含 40px 缓冲 (20px top + 20px bottom)
      // 使用 top: 20px 确保 AddButton 与 Active Item (Scale 1) 顶部对齐
      // 从而保证图标在同一水平线上，不受内容高度差异（如电池 vs Spacer）影响
      top: '20px',
    }
  }

  return {
    currentIndex,
    activeIndex,
    next,
    prev,
    goTo,
    getItemStyle,
    setDragOffset,
    endDrag,
    getAppendIconStyle,
  }
}
